import { useState, useEffect, useCallback, useRef } from 'react';

type GeolocationState = {
  coordinates: { lat: number; lng: number } | null;
  accuracy: number | null;
  address: string | null;
  permissionState: 'prompt' | 'granted' | 'denied' | 'unavailable';
  isLoading: boolean;
  error: string | null;
};

type NominatimResponse = {
  address?: {
    road?: string;
    pedestrian?: string;
    suburb?: string;
    neighbourhood?: string;
    village?: string;
    hamlet?: string;
    city?: string;
    town?: string;
    municipality?: string;
    county?: string;
    state?: string;
  };
  display_name?: string;
};

// Round coordinates to ~100m precision for privacy (0.001° ≈ 111m at equator, ~93m at Australian latitudes)
export const roundToHundredMeters = (coord: number): number =>
  Math.round(coord * 1000) / 1000;

const reverseGeocode = async (lat: number, lng: number): Promise<string | null> => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&zoom=14`,
      {
        headers: {
          'Accept-Language': 'en',
          'User-Agent': 'CharlieApp-SafeguardingYou/1.0',
        },
      }
    );
    if (!response.ok) return null;
    const raw: unknown = await response.json();
    const data = raw as NominatimResponse;
    const a = data.address ?? {};
    const parts = [
      a.road ?? a.pedestrian,
      a.suburb ?? a.neighbourhood ?? a.village ?? a.hamlet,
      a.city ?? a.town ?? a.municipality ?? a.county,
      a.state,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : (data.display_name ?? null);
  } catch {
    return null;
  }
};

export const useGeolocation = () => {
  const [state, setState] = useState<GeolocationState>({
    coordinates: null,
    accuracy: null,
    address: null,
    permissionState: 'prompt',
    isLoading: false,
    error: null,
  });

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Check current permission state on mount
  useEffect(() => {
    if (!navigator.geolocation) {
      setState((prev) => ({ ...prev, permissionState: 'unavailable' }));
      return;
    }

    if (!navigator.permissions) return;

    let permissionStatus: PermissionStatus | null = null;

    const handleChange = () => {
      if (permissionStatus && mountedRef.current) {
        setState((prev) => ({
          ...prev,
          permissionState: permissionStatus!.state as 'prompt' | 'granted' | 'denied',
        }));
      }
    };

    navigator.permissions
      .query({ name: 'geolocation' })
      .then((result) => {
        permissionStatus = result;
        if (mountedRef.current) {
          setState((prev) => ({
            ...prev,
            permissionState: result.state as 'prompt' | 'granted' | 'denied',
          }));
        }
        result.addEventListener('change', handleChange);
      })
      .catch(() => {
        // permissions API not supported, keep 'prompt'
      });

    return () => {
      if (permissionStatus) {
        permissionStatus.removeEventListener('change', handleChange);
      }
    };
  }, []);

  // Explicit request — does NOT auto-fire
  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setState((prev) => ({
        ...prev,
        permissionState: 'unavailable',
        error: 'Geolocation is not supported by this browser',
      }));
      return;
    }

    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        // Use raw coordinates for reverse geocoding accuracy, store rounded for privacy
        const rawLat = position.coords.latitude;
        const rawLng = position.coords.longitude;
        const lat = roundToHundredMeters(rawLat);
        const lng = roundToHundredMeters(rawLng);
        const address = await reverseGeocode(rawLat, rawLng);
        if (!mountedRef.current) return;
        setState({
          coordinates: { lat, lng },
          accuracy: position.coords.accuracy,
          address,
          permissionState: 'granted',
          isLoading: false,
          error: null,
        });
      },
      (err) => {
        if (!mountedRef.current) return;
        const permissionState = err.code === err.PERMISSION_DENIED ? 'denied' : 'prompt';
        setState((prev) => ({
          ...prev,
          permissionState,
          isLoading: false,
          error: err.message,
        }));
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000,
      }
    );
  }, []);

  return { ...state, requestLocation };
};
